import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { convertWeightForPricing } from '@rematter/modules/trade/utils/weight';
import { IconButton } from '@rematter/ui-web';
import { cn, weightToPounds } from '@rematter/ui-web/lib';
import { useSetAtom, useAtomValue } from 'jotai';
import { LucideCornerDownRight, Trash2 } from 'lucide-react';

import { NumericInputCell } from '../NumericInputCell';

import { gridLayoutMaterialReconcilitation } from './constants/gridLayout';
import { DeductionField } from './DeductionField';

import {
  $updateDeduction,
  $removeDeduction,
  ReconciliationDeductionForm,
  $materialsReconciliationForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { DeductionTypeRecord } from '~/modules/trade/types/deductionType';
import { zeroMoney } from '~/modules/trade/utils/money';

interface ReceivedDeductionLineItemProps {
  deduction: ReconciliationDeductionForm;
  deductionTypes: DeductionTypeRecord[];
  lineItemOptions: { id: string; label: string }[];
  hideSecondInput?: boolean;
  dialogMode?: boolean;
  disabled?: boolean;
}

export const ReceivedDeductionLineItemForm = ({
  deduction,
  deductionTypes,
  lineItemOptions,
  hideSecondInput,
  disabled = false,
}: ReceivedDeductionLineItemProps) => {
  const updateDeduction = useSetAtom($updateDeduction);
  const removeDeduction = useSetAtom($removeDeduction);
  const formState = useAtomValue($materialsReconciliationForm);

  // Find the associated received line item
  const receivedLineItem = React.useMemo(
    () =>
      formState.receivedLineItems.find(
        (item) => item.id === deduction.receivedLineItemId,
      ),
    [formState.receivedLineItems, deduction.receivedLineItemId],
  );

  const deductionType = deductionTypes.find(
    (dt) => dt.id === deduction.deductionTypeId,
  );
  const isWeightDeduction = deductionType?.applicationType === 'Weight';

  // For weight deduction, price is auto-calculated
  const calculatedPrice = deduction.priceQuantity?.amount || 0;

  const handleDeductionTypeChange = (id: string) => {
    updateDeduction(deduction.id, { deductionTypeId: id });
  };

  const handleLineItemChange = (id: string) => {
    updateDeduction(deduction.id, { receivedLineItemId: id });
  };

  const handleWeightChange = (value: number) => {
    const deductionType = deductionTypes.find(
      (dt) => dt.id === deduction.deductionTypeId,
    );

    if (deductionType?.applicationType === 'Weight') {
      if (isWeightDeduction && receivedLineItem) {
        const deductionWeightPounds = weightToPounds({
          value,
          unit: deduction.weightQuantity?.unit ?? enumUnit.Pounds,
          __typename: 'Weight',
        });

        const convertedDeductionWeight = convertWeightForPricing(
          deductionWeightPounds,
          receivedLineItem?.pricingUnit ?? enumUnit.Pounds,
        );

        updateDeduction(deduction.id, {
          weightQuantity: {
            value,
            unit: enumUnit.Pounds,
            __typename: 'Weight',
          },
          priceQuantity: {
            ...zeroMoney(),
            amount:
              convertedDeductionWeight *
              (receivedLineItem.unitPrice?.amount || 0),
          },
        });
      } else {
        updateDeduction(deduction.id, {
          priceQuantity: {
            ...zeroMoney(),
            amount: value,
          },
        });
      }
    }
  };

  const handlePriceChange = (value: number) => {
    const deductionType = deductionTypes.find(
      (dt) => dt.id === deduction.deductionTypeId,
    );

    if (deductionType?.applicationType === 'Weight') {
      updateDeduction(deduction.id, {
        weightQuantity: { value, unit: enumUnit.Pounds, __typename: 'Weight' },
        priceQuantity: {
          ...zeroMoney(),
          amount: value * (receivedLineItem?.unitPrice?.amount || 0),
        },
      });
    } else {
      updateDeduction(deduction.id, {
        priceQuantity: {
          ...zeroMoney(),
          amount: value,
        },
      });
    }
  };

  const deductionTypeOptions = React.useMemo(
    () =>
      deductionTypes.map((type) => ({
        id: type.id,
        label: type.name,
      })),
    [deductionTypes],
  );

  return (
    <div
      className={cn(
        '@ReceivedDeductionLineItemForm',
        'mt-2 grid w-full items-center gap-2',
        gridLayoutMaterialReconcilitation,
      )}
    >
      {/* Column 1: Deduction Item */}
      <div className="grid w-full grid-cols-[40px_1fr] gap-2">
        <div className="flex w-[40px] items-center justify-center rounded-md bg-failure/10 text-failure">
          <LucideCornerDownRight size={18} />
        </div>
        <DeductionField
          className="text-failure focus-within:border-failure"
          selectedDeductionTypeId={deduction.deductionTypeId}
          deductionTypeOptions={deductionTypeOptions}
          onChangeDeductionType={handleDeductionTypeChange}
          selectedLineItemId={deduction.receivedLineItemId}
          lineItemOptions={lineItemOptions}
          onChangeLineItem={handleLineItemChange}
          hideSecondInput={hideSecondInput}
          disabled={disabled}
        />
      </div>
      {/* Column 2-5: Empty */}
      <div />
      <div />
      <div />
      <div />
      {/* Column 6: Weight Deduction / Empty */}
      {isWeightDeduction ? (
        <NumericInputCell
          className="!*:ring-failure/40 !*:ring-offset-failure/10 !*:outline-none !*:focus-within:border-failure text-failure"
          value={deduction.weightQuantity?.value || 0}
          onChange={handleWeightChange}
          prefix="W"
          suffix="lb"
          isDisabled={disabled}
        />
      ) : (
        <div />
      )}
      {/* Column 7: Deduction Amount */}
      <NumericInputCell
        className={cn(
          'text-failure focus-within:border-failure focus-within:ring-failure focus:ring-failure',
          isWeightDeduction && '[&_.group]:!bg-failure/10',
        )}
        value={calculatedPrice}
        onChange={handlePriceChange}
        isDisabled={isWeightDeduction || disabled}
        prefix="$"
      />
      {/* Column 8-9: Empty */}
      <div />
      <div />
      {/* Column 10: Actions */}
      {!disabled && (
        <IconButton
          icon={<Trash2 size={18} />}
          variant="outline"
          className="size-[40px]"
          color="failure"
          aria-label="Delete deduction"
          onPress={() => removeDeduction(deduction.id)}
          isDisabled={disabled}
        />
      )}
    </div>
  );
};
